import { CredentialsResponse, SessionHandle } from 'src/utilities/types';
import { Platform } from 'src/environment';
import { NativeMessages } from 'src/native-messages';
import {
  extractMinimalAuthData,
  GHSDK_DATA_KEY,
} from 'src/utilities/network-utility';
import { get } from '../dependencies/nodash/get';
import { appConfigFactory } from '../factories/appConfig.factory';
import { windowFactory } from '../factories/window.factory';
import { EventsManagerImpl } from '../utilities/events-manager.utility';
import { LocalStorageUtility } from '../utilities/localStorage.utility';

export interface CredentialService {
  setCredentials(credential: CredentialsResponse): void;
  clearCredentials(): void;
  getCredentialsAsync(): Promise<CredentialsResponse>;
  subscribeToNativeSetCredentialsMessage(callback?: () => void): void;
  unsubscribeToNativeSetCredentialsMessage(): void;
  getCredentials(): CredentialsResponse;
  getCredentialUdid(): string;
  getCredentialEmail(): string;
  getCredentialFirstName(): string;
  getCredentialLastName(): string;
  isAnonymous(): boolean;
  isLoggedIn(): boolean;
  isTokenExpired(): boolean;
  getCookieOptoutFlag(): boolean;
  isInternalUser(): boolean;
  isCorporateUser(): boolean;
}

export class CredentialServiceImpl implements CredentialService {
  private static readonly NATIVE_INIT_TIMEOUT_MS = 5000;
  private static instance: CredentialService;

  public static getInstance(): CredentialService {
    if (!CredentialServiceImpl.instance) {
      CredentialServiceImpl.instance = new CredentialServiceImpl();
    }

    return CredentialServiceImpl.instance;
  }

  private credentials: CredentialsResponse;
  private storageUtility = new LocalStorageUtility({
    storagePrefix: null,
  });
  private windowInstance = windowFactory();
  private appConfig = appConfigFactory();
  private eventsManager = EventsManagerImpl.getInstance();
  private unsubscribeHandler: () => void;
  private setCredentialsMsgDeferred: { resolve: Function } = null;
  private setCredentialsMsgLock: Promise<void> = null;
  private credentialsWaitTimer: number;

  public setCredentials(credentials: CredentialsResponse): void {
    // Store full credentials in memory
    this.credentials = credentials;

    // Store entire session_handle and ud_id in localStorage with nested structure
    // Maintains paths:
    //   - auth.credentials.session_handle (entire object)
    //   - auth.credentials.credential.ud_id
    if (credentials?.session_handle?.access_token) {
      const minimalAuthData = extractMinimalAuthData(credentials);

      localStorage.setItem(GHSDK_DATA_KEY, JSON.stringify(minimalAuthData));
    }
  }

  public clearCredentials(): void {
    this.credentials = null;
    localStorage.removeItem(GHSDK_DATA_KEY);
  }

  public getCredentials(): CredentialsResponse {
    return this.credentials || this.getBlankCredentials();
  }

  private getBlankCredentials(): CredentialsResponse {
    return {
      session_handle: {} as SessionHandle,
    };
  }

  public getCredentialsAsync(): Promise<CredentialsResponse> {
    return new Promise<CredentialsResponse>((resolve) => {
      if (this.appConfig.platform === Platform.WEB) {
        resolve(this.getCredentials());
      } else {
        this.waitForNativeSetCredentialsMessage().then(() => {
          resolve(this.getCredentials());
        });
      }
    });
  }

  private waitForNativeSetCredentialsMessage(): Promise<void> {
    if (!this.setCredentialsMsgLock) {
      this.initializeSetCredentialsDeferredPromise();
    }

    return this.setCredentialsMsgLock;
  }

  private initializeSetCredentialsDeferredPromise(): void {
    this.setCredentialsMsgLock = new Promise((resolve) => {
      this.setCredentialsMsgDeferred = { resolve };
    });
  }

  public subscribeToNativeSetCredentialsMessage(
    credentialsSetCallback?: () => void,
  ): void {
    if (!this.setCredentialsMsgLock) {
      this.initializeSetCredentialsDeferredPromise();
    }

    const unsubscribeHandler = this.eventsManager.subscribe(
      NativeMessages.SET_CREDENTIALS,
      (payload) => {
        this.setCredentials(payload.credentials);
        this.setCredentialsMsgDeferred.resolve();
        if (credentialsSetCallback) {
          credentialsSetCallback();
        }
      },
    );

    this.unsubscribeHandler = () => {
      unsubscribeHandler();
      this.setCredentialsMsgDeferred.resolve();
    };
    this.credentialsWaitTimer = this.windowInstance.setTimeout(
      () => this.setCredentialsMsgDeferred.resolve(),
      CredentialServiceImpl.NATIVE_INIT_TIMEOUT_MS,
    );
  }

  public unsubscribeToNativeSetCredentialsMessage(): void {
    if (this.unsubscribeHandler) {
      this.unsubscribeHandler();
      this.unsubscribeHandler = null;
    }

    this.windowInstance.clearTimeout(this.credentialsWaitTimer);
    this.credentialsWaitTimer = null;
    this.setCredentialsMsgLock = null;
    this.setCredentialsMsgDeferred = null;
  }

  public getCredentialUdid(): string {
    return get(this.getCredentials(), 'credential.ud_id', '');
  }

  public getCredentialEmail(): string {
    return get(this.getCredentials(), 'credential.email', '') ?? '';
  }

  public getCredentialFirstName(): string {
    return get(this.getCredentials(), 'credential.first_name', '');
  }

  public getCredentialLastName(): string {
    return get(this.getCredentials(), 'credential.last_name', '');
  }

  public getCookieOptoutFlag(): boolean {
    const details = this.storageUtility.get('ngStorage-dinerDetails');

    return (
      details?.preferences?.other?.privacycontrol?.information_sell === 'denied'
    );
  }

  public isAnonymous(): boolean {
    return !this.getCredentialUdid();
  }

  public isLoggedIn(): boolean {
    return !this.isAnonymous();
  }

  public isTokenExpired() {
    return (
      new Date().getTime() >
      get(this.getCredentials(), 'session_handle.token_expire_time')
    );
  }

  public isInternalUser(): boolean {
    const userEmail = this.getCredentialEmail();
    const emailDomain = userEmail.substring(userEmail.lastIndexOf('@') + 1);

    return this.isLoggedIn() && emailDomain.toLowerCase() === 'grubhub.com';
  }

  public isCorporateUser(): boolean {
    return !!get(this.getCredentials(), 'isCorpUser', false);
  }
}
